import { CodeBlock } from './CodeBlock.js';

/**
 * End-of-post conversion card. Every post ends here — the
 * single moment a reader has finished an article that argues
 * for Memento is the highest-intent moment we'll get out of
 * them, and the card is sized to dominate that beat without
 * shouting.
 *
 * Visual reads like a quieter echo of the landing hero:
 *   - mono eyebrow ("~/try_memento")
 *   - one-line install snippet via the same CodeBlock used in
 *     the hero, so the click-to-copy affordance is consistent
 *   - primary "View on GitHub" link plus secondary docs link
 *
 * The card itself uses the same `max-w-prose` width as the
 * post body so the reader's eye doesn't have to re-center
 * when the article ends.
 */
const GITHUB_URL = 'https://github.com/veerps57/memento';
const NPM_URL = 'https://www.npmjs.com/package/@psraghuveer/memento';
const DOCS_URL = `${GITHUB_URL}#getting-started`;

export function PostFooterCTA(): JSX.Element {
  return (
    <aside
      aria-labelledby="post-cta-heading"
      className="mx-auto mt-16 max-w-prose rounded-lg border border-border bg-bg/40 p-6 md:p-8"
    >
      <p className="font-mono text-xs uppercase tracking-widish text-muted">~/try_memento</p>
      <h2
        id="post-cta-heading"
        className="mt-3 text-2xl font-medium leading-tight tracking-tight text-fg md:text-3xl"
      >
        One memory layer for every AI assistant you use.
      </h2>
      <p className="mt-4 text-base leading-relaxed text-fg/80">
        Memento runs an MCP server over a single SQLite file on your machine. Works with Claude
        Code, Cursor, Copilot, Cline, OpenCode, Aider, and any MCP-capable client. Apache-2.0.
      </p>
      <div className="mt-6">
        <CodeBlock command="npx @psraghuveer/memento init" />
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <a
          href={GITHUB_URL}
          className="inline-flex items-center rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent/90"
        >
          Star on GitHub
        </a>
        <a
          href={DOCS_URL}
          className="inline-flex items-center rounded-md border border-border px-4 py-2 text-sm text-fg transition-colors hover:border-fg"
        >
          Read the docs
        </a>
        <a
          href={NPM_URL}
          className="text-sm text-muted underline decoration-border underline-offset-4 hover:text-fg hover:decoration-fg"
        >
          npm
        </a>
      </div>
    </aside>
  );
}
